/**
 * Exit-интервью увольнения (ТЗ-2026-008 шаг 7, ответы аналитика 07.10.2026): HR, проверивший
 * заявку, фиксирует основную причину ухода, комментарий и (необязательно) анкету — или отметку
 * «сотрудник отказался / недоступен» с пояснением. Результаты видят только HR компании и CEO.
 * Правила обязательности — зеркало сервера (decideTask), источник правды — он.
 */
import { useRef, useState } from 'react'
import { Platform } from 'react-native'
import { Text, YStack } from 'tamagui'
import { useQueryClient } from '@tanstack/react-query'

import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import { pickFileWeb } from '../../../lib/files'
import { Button, FormTextArea, Icon, OptionSelect } from '../../../ui'
import { errorText } from '../errorText'
import { EXIT_REASONS, type ExitReason } from '../termination-types'

export function ExitInterviewForm({
  taskId,
  requestId,
  onDone,
}: {
  taskId: string
  requestId: string
  onDone: () => void
}) {
  const { t } = useApp()
  const qc = useQueryClient()
  const [refused, setRefused] = useState(false)
  const [reason, setReason] = useState<ExitReason | undefined>()
  const [comment, setComment] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const lock = useRef(false)

  const needComment = refused || reason === 'other'
  const ready = refused ? !!comment.trim() : !!reason && (!needComment || !!comment.trim())

  const save = async () => {
    if (!ready || lock.current) return
    lock.current = true
    setPending(true)
    setError('')
    try {
      let uploadId: string | undefined
      if (file && !refused) {
        const form = new FormData()
        form.append('file', file as unknown as Blob)
        uploadId = (await api.uploadEdoDoc(form)).id
      }
      await api.exitInterview(taskId, {
        refused: refused || undefined,
        reason: refused ? undefined : reason,
        comment: comment.trim() || undefined,
        uploadId,
      })
      // Страница остаётся открытой — карточку и списки обновляем сами (useDecideTask этого не делает).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['edo-request', requestId] }),
        qc.invalidateQueries({ queryKey: ['edo-requests'] }),
        qc.invalidateQueries({ queryKey: ['edo-tasks'] }),
      ])
      onDone()
    } catch (e) {
      setError(t(errorText(e)))
    } finally {
      lock.current = false
      setPending(false)
    }
  }

  return (
    <YStack gap={10}>
      <Text fontSize={14} fontWeight="700" color="$color">
        {t({ ru: 'Exit-интервью', en: 'Exit interview', az: 'Exit-müsahibə' })}
      </Text>
      <Text fontSize={12} color="$muted">
        {t({
          ru: 'Результаты увидят только HR компании и CEO. Сотрудник и руководители их не видят.',
          en: 'Only company HR and the CEO will see the results. The employee and managers will not.',
          az: 'Nəticələri yalnız şirkətin HR-ı və CEO görəcək. İşçi və rəhbərlər görməyəcək.',
        })}
      </Text>
      <Button
        variant={refused ? 'primary' : 'default'}
        icon={<Icon name={refused ? 'check' : 'x'} size={15} color={refused ? '$onPrimary' : '$muted'} />}
        onPress={() => setRefused(!refused)}
      >
        {t({ ru: 'Сотрудник отказался от интервью или недоступен', en: 'Employee declined or unavailable', az: 'İşçi müsahibədən imtina etdi və ya əlçatan deyil' })}
      </Button>
      {!refused && (
        <YStack gap={4}>
          <Text fontSize={12} fontWeight="600" color="$muted">
            {t({ ru: 'Основная причина ухода *', en: 'Main reason for leaving *', az: 'Getməyin əsas səbəbi *' })}
          </Text>
          <OptionSelect
            options={EXIT_REASONS.map((r) => ({ value: r.value, label: t(r.label) }))}
            value={reason ? [reason] : []}
            onChange={(next) => setReason(next[0] as ExitReason | undefined)}
          />
        </YStack>
      )}
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">
          {refused
            ? t({ ru: 'Почему интервью не проведено *', en: 'Why the interview did not happen *', az: 'Müsahibə niyə keçirilmədi *' })
            : needComment
              ? t({ ru: 'Комментарий *', en: 'Comment *', az: 'Şərh *' })
              : t({ ru: 'Комментарий', en: 'Comment', az: 'Şərh' })}
        </Text>
        <FormTextArea value={comment} onChangeText={setComment} maxLength={4000} />
      </YStack>
      {!refused && Platform.OS === 'web' && (
        <Button
          icon={<Icon name="paperclip" size={15} color="$muted" />}
          onPress={async () => setFile(await pickFileWeb('.pdf,.doc,.docx,.jpg,.jpeg,.png'))}
        >
          {file
            ? `📎 ${file.name}`
            : t({ ru: 'Приложить анкету или протокол (необязательно)', en: 'Attach questionnaire (optional)', az: 'Anket əlavə et (könüllü)' })}
        </Button>
      )}
      {!!error && <Text fontSize={12} color="$red10">{error}</Text>}
      <Button variant="primary" disabled={!ready || pending} onPress={save}>
        {t({ ru: 'Сохранить результаты и передать CEO', en: 'Save and send to CEO', az: 'Saxla və CEO-ya göndər' })}
      </Button>
    </YStack>
  )
}
